import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';

const SkillCard = ({ item, type }) => {
    const isStrength = type === 'strength';
    const severity = item.severity || 5;
    
    // Logic for mastery label
    const getLabel = (sev) => {
        if (isStrength) {
            if (sev >= 9) return 'Mastered';
            if (sev >= 7) return 'Expert';
            if (sev >= 5) return 'Proficient';
            return 'Strong';
        } else {
            if (sev >= 8) return 'Critical';
            if (sev >= 6) return 'Significant';
            if (sev >= 4) return 'Moderate';
            return 'Needs Work';
        }
    };

    return (
        <div className={`relative overflow-hidden p-4 rounded-xl border transition-all duration-300 hover:shadow-md ${
            isStrength 
                ? 'bg-emerald-50/30 border-emerald-100/50 hover:bg-emerald-50/60' 
                : 'bg-amber-50/30 border-amber-100/50 hover:bg-amber-50/60'
        }`}>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                        isStrength ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'
                    }`}>
                        <Icon name={isStrength ? 'Check' : 'AlertTriangle'} size={14} />
                    </div>
                    <span className="text-[13px] font-normal text-slate-800 truncate max-w-[120px]">
                        {item.topic || item}
                    </span>
                </div>
                <span className={`text-[10px] font-normal tracking-wider px-2 py-0.5 rounded-full ${
                    isStrength ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                }`}>
                    {getLabel(severity)}
                </span>
            </div>

            <div className="space-y-1.5">
                <div className="flex justify-between text-[10px] font-normal text-slate-400">
                    <span>{isStrength ? 'Mastery' : 'Severity'}</span>
                    <span>{severity}/10</span>
                </div>
                <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div 
                        className={`h-full rounded-full transition-all duration-500 ${isStrength ? 'bg-emerald-500' : 'bg-amber-500'}`}
                        style={{ width: `${(severity / 10) * 100}%` }}
                    />
                </div>
            </div>
        </div>
    );
};

const StrengthsWeaknesses = ({ strengths = [], weaknesses = [] }) => {
    const [activeTab, setActiveTab] = useState('strengths');
    const [isModalOpen, setIsModalOpen] = useState(false);

    const items = activeTab === 'strengths' ? strengths : weaknesses;
    const snippet = items.slice(0, 3);
    const hasMore = items.length > 3;

    return (
        <>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                {/* Header with Tabs */}
                <div className="p-2 border-b border-slate-100 bg-slate-50/50 flex gap-1">
                    <button 
                        onClick={() => setActiveTab('strengths')}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-[11px] font-normal transition-all ${
                            activeTab === 'strengths' 
                                ? 'bg-white text-emerald-600 shadow-sm border border-slate-200' 
                                : 'text-slate-400 hover:text-slate-600'
                        }`}
                    >
                        <Icon name="CheckCircle" size={12} />
                        Strengths
                    </button>
                    <button 
                        onClick={() => setActiveTab('weaknesses')}
                        className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-[11px] font-normal transition-all ${
                            activeTab === 'weaknesses' 
                                ? 'bg-white text-amber-600 shadow-sm border border-slate-200' 
                                : 'text-slate-400 hover:text-slate-600'
                        }`}
                    >
                        <Icon name="AlertCircle" size={12} />
                        Weaknesses
                    </button>
                </div>

                {/* Content Snippet */}
                <div className="p-4 space-y-3">
                    {snippet.length > 0 ? (
                        snippet.map((item, idx) => (
                            <SkillCard key={idx} item={item} type={activeTab === 'strengths' ? 'strength' : 'weakness'} />
                        ))
                    ) : (
                        <div className="py-8 text-center px-4">
                            <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3">
                                <Icon name="Sparkles" size={20} className="text-slate-300" />
                            </div>
                            <p className="text-[12px] font-normal text-slate-400">Complete more interviews to unlock {activeTab} insights</p>
                        </div>
                    )}
                </div>

                {/* View More Footer */}
                <div className="p-3 bg-slate-50/50 border-t border-slate-100">
                    <button 
                        onClick={() => setIsModalOpen(true)}
                        className="w-full py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-[11px] font-normal text-slate-600 flex items-center justify-center gap-2 transition-all group"
                    >
                        {items.length > 0 ? (
                            <>
                                View Full Report
                                <div className="w-4 h-4 rounded-full bg-slate-100 flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                                    <Icon name="ChevronRight" size={10} />
                                </div>
                            </>
                        ) : (
                            'View Skill Roadmap'
                        )}
                    </button>
                </div>
            </div>

            {/* Full Insights Modal */}
            <Modal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)}
                title="Skill Insights & Mastery"
                size="lg"
            >
                <div className="space-y-8 py-4">
                    {/* Strengths Section in Modal */}
                    <div>
                        <div className="flex items-center gap-2 mb-4">
                            <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600">
                                <Icon name="TrendingUp" size={16} />
                            </div>
                            <h3 className="text-sm font-normal text-slate-900 tracking-wide">Identified Strengths</h3>
                            <span className="text-[10px] font-normal bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full ml-auto">
                                {strengths.length} Total
                            </span>
                        </div>
                        {strengths.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {strengths.map((item, idx) => (
                                    <SkillCard key={idx} item={item} type="strength" />
                                ))}
                            </div>
                        ) : (
                            <div className="bg-slate-50 rounded-2xl p-8 text-center border-2 border-dashed border-slate-200">
                                <p className="text-sm text-slate-400 font-normal">Keep practicing to identify your core strengths!</p>
                            </div>
                        )}
                    </div>

                    <div className="h-px bg-slate-100" />

                    {/* Weaknesses Section in Modal */}
                    <div>
                        <div className="flex items-center gap-2 mb-4">
                            <div className="p-1.5 rounded-lg bg-amber-50 text-amber-600">
                                <Icon name="Target" size={16} />
                            </div>
                            <h3 className="text-sm font-normal text-slate-900 tracking-wide">Growth Opportunities</h3>
                            <span className="text-[10px] font-normal bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full ml-auto">
                                {weaknesses.length} Total
                            </span>
                        </div>
                        {weaknesses.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {weaknesses.map((item, idx) => (
                                    <SkillCard key={idx} item={item} type="weakness" />
                                ))}
                            </div>
                        ) : (
                            <div className="bg-slate-50 rounded-2xl p-8 text-center border-2 border-dashed border-slate-200">
                                <p className="text-sm text-slate-400 font-normal">No critical weaknesses identified yet.</p>
                            </div>
                        )}
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default StrengthsWeaknesses;
